import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ScrapejobApiClient } from 'api/scrapejob';
import { ScrapejobModel } from 'models/scrapejob/ScrapejobModel';
import { ScrapejobSummaryModel } from 'models/scrapejob/ScrapejobSummaryModel';
import { PaginationModel } from 'models';
import { ProcessStatus } from 'models/types';
import { NavigationService } from 'services';

export function useScrapejobs() {
  return useQuery<ScrapejobSummaryModel[]>({
    queryKey: ['scrapejobs'],
    queryFn: ScrapejobApiClient.getAllScrapejobs
  });
}

export function usePendingScrapejobs() {
  return useQuery<ScrapejobSummaryModel[]>({
    queryKey: ['scrapejobs', 'pending'],
    queryFn: () => ScrapejobApiClient.getScrapejobsByStatus('Pending')
  });
}

export function useScrapejobDetails(id: string) {
  return useQuery<ScrapejobModel>({
    queryKey: ['scrapejob-details', id],
    queryFn: () => ScrapejobApiClient.getScrapejobById(id),
    enabled: !!id
  });
}

export function useDetailedStatusScrapejobs(status: ProcessStatus, page: number = 1, perPage: number = 100) {
  return useQuery<PaginationModel<ScrapejobModel>>({
    queryKey: ['scrapejobs-detailed-status', status, page, perPage],
    queryFn: () => ScrapejobApiClient.getDetailedStatusScrapejobs(status, page, perPage),
    enabled: !!status
  });
}

export function useApproveScrapejob() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: (payload: any) => ScrapejobApiClient.approveScrapejob(payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch scrapejob details and scrapejobs list
      queryClient.invalidateQueries({ queryKey: ['scrapejob-details', variables.scrapejob_id] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs'] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs-detailed-status'] });
      toast({
        title: 'Scrapejob approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(NavigationService.getScrapejobDetailsPath(variables.scrapejob_id));
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to approve scrapejob',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useRejectScrapejob() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: any) => ScrapejobApiClient.rejectScrapejob(payload),
    onSuccess: (data, variables) => {
      // Invalidate and refetch scrapejob details and scrapejobs list
      queryClient.invalidateQueries({ queryKey: ['scrapejob-details', variables.scrapejob_id] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs'] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs-detailed-status'] });
      toast({
        title: 'Scrapejob rejected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reject scrapejob',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useSanitizeScrapejob() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: any) => ScrapejobApiClient.sanitizeScrapejob(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scrapejob-details', variables.scrapejob_id] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs'] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs-detailed-status'] });
      toast({
        title: 'Scrapejob sanitized successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to sanitize scrapejob',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

// Bulk operations
export function useBulkApproveScrapejobs() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (scrapejobIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        scrapejobIds.map(id => ScrapejobApiClient.approveScrapejob({ scrapejob_id: id }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      // Single invalidation after all operations complete
      queryClient.invalidateQueries({ queryKey: ['scrapejobs'] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs-detailed-status'] });

      // Single toast with summary
      toast({
        title: `Bulk approve completed`,
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} scrapejob${succeeded !== 1 ? 's' : ''} approved`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk approve failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useBulkRejectScrapejobs() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (scrapejobIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        scrapejobIds.map(id => ScrapejobApiClient.rejectScrapejob({ scrapejob_id: id }))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      // Single invalidation after all operations complete
      queryClient.invalidateQueries({ queryKey: ['scrapejobs'] });
      queryClient.invalidateQueries({ queryKey: ['scrapejobs-detailed-status'] });

      // Single toast with summary
      toast({
        title: `Bulk reject completed`,
        description: failed > 0
          ? `${succeeded} succeeded, ${failed} failed`
          : `${succeeded} scrapejob${succeeded !== 1 ? 's' : ''} rejected`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk reject failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}
